import { CSSProperties, ComponentPropsWithoutRef, forwardRef } from 'react';
import styled from 'styled-components';

import { Breakpoint, breakpoints, MaybeResponsive } from '@/shared/styles';

import { Box, BoxProps } from '../Box';

interface FlexOptions extends BoxProps {
  display?: MaybeResponsive<CSSProperties['display']>;
  flex?: MaybeResponsive<CSSProperties['flex']>;
  align?: MaybeResponsive<CSSProperties['alignItems']>;
  justify?: CSSProperties['justifyContent'];
  wrap?: CSSProperties['flexWrap'];
  direction?: MaybeResponsive<CSSProperties['flexDirection']>;
  basis?: CSSProperties['flexBasis'];
  grow?: CSSProperties['flexGrow'];
  shrink?: CSSProperties['flexShrink'];
  gap?: CSSProperties['gap'];
}

const FlexBox = styled(Box)`
  ${({
    $alignItems,
    $flexDirection,
    $display,
  }: {
    $alignItems: FlexOptions['align'];
    $flexDirection: FlexOptions['direction'];
    $display: FlexOptions['display'];
  }) => {
    let styles = '';
    const breakpointsProps: { [key: string]: [string, string][] } = { default: [] };

    if (typeof $alignItems === 'string') {
      breakpointsProps.default.push(['align-items', $alignItems]);
    } else {
      for (const breakpointKey in $alignItems) {
        if (!breakpointsProps[breakpointKey]) {
          breakpointsProps[breakpointKey] = [];
        }
        breakpointsProps[breakpointKey].push([
          'align-items',
          $alignItems[breakpointKey as Breakpoint] || '',
        ]);
      }
    }

    if (typeof $flexDirection === 'string') {
      breakpointsProps.default.push(['flex-direction', $flexDirection]);
    } else {
      for (const breakpointKey in $flexDirection) {
        if (!breakpointsProps[breakpointKey]) {
          breakpointsProps[breakpointKey] = [];
        }
        breakpointsProps[breakpointKey].push([
          'flex-direction',
          $flexDirection[breakpointKey as Breakpoint] || '',
        ]);
      }
    }

    if (typeof $display === 'string') {
      breakpointsProps.default.push(['display', $display]);
    } else {
      for (const breakpointKey in $display) {
        if (!breakpointsProps[breakpointKey]) {
          breakpointsProps[breakpointKey] = [];
        }
        breakpointsProps[breakpointKey].push([
          'display',
          $display[breakpointKey as Breakpoint] || '',
        ]);
      }
    }

    // Build styles
    for (const breakpointKey in breakpointsProps) {
      if (breakpointKey === 'default') {
        styles += breakpointsProps[breakpointKey]
          .map(props => `${props[0]}: ${props[1]};\n`)
          .join('');
      } else {
        styles += `@media (min-width: ${breakpoints[breakpointKey as Breakpoint]}px) {
          ${breakpointsProps[breakpointKey].map(props => `${props[0]}: ${props[1]};\n`).join('')}
        }`;
      }
    }

    return styles;
  }}
`;

export interface FlexProps extends Omit<ComponentPropsWithoutRef<'div'>, 'color'>, FlexOptions {}

export const Flex = forwardRef<HTMLDivElement, FlexProps>(function Flex(props, ref) {
  const {
    display = 'flex',
    direction,
    align,
    justify,
    wrap,
    basis,
    grow,
    shrink,
    gap,
    flex,
    borderRadius,
    ...rest
  } = props;

  return (
    <FlexBox
      ref={ref}
      {...rest}
      $flexDirection={direction}
      $display={display}
      $alignItems={align}
      style={{
        justifyContent: justify,
        flexWrap: wrap,
        flexBasis: basis,
        flexGrow: grow,
        flexShrink: shrink,
        gap,
        flex,
        borderRadius,
        ...props.style,
      }}
    />
  );
});

Flex.displayName = 'Flex';
